import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BuildingOfficeIcon,
  GlobeAltIcon,
  IdentificationIcon,
  PhoneIcon,
  UserCircleIcon,
} from "@heroicons/react/20/solid";
import FormInput from "../../../components/controls/FormInput";
import Autocomplete from "../../../components/Places/PlacesAutocomplete";
import ImageUploader from "../../../components/controls/ImageUploader";
import { addOrUpdateCompany } from "../../../db/Companies";
import Toggle from "../../../components/ui/Toggle";

export default function AdminCompanyEdit({ isOpen, onClose, company, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (company) {
      setForm({
        ...company,
        tempImageUrl: company.imageUrl || "",
        hasChangedImageUrl: false,
        isActive: company.isActive ?? true,
      });
    }
  }, [company]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      if (field === "location") {
        return { ...prev, location: { ...prev.location, ...value } };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      imageUrl: form.hasChangedImageUrl
        ? form.tempImageUrl ?? null
        : form.imageUrl,
    };

    try {
      const updated = await addOrUpdateCompany(payload);
      onSave({ ...payload, id: updated });
    } catch (err) {
      console.error(err);
      alert(t("errors.submission"));
    }
  };

  if (!isOpen || !form) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 overflow-y-auto text-primary">
      <div className="min-h-screen flex flex-col items-center justify-start py-4 px-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl p-6 relative">
          <button
            onClick={onClose}
            className="btn btn-sm btn-circle absolute top-2 right-2"
          >
            ✕
          </button>

          <h2 className="text-2xl font-bold mb-4 text-center">
            {t("adminUserTable.companyManager")}
          </h2>

          <form
            onSubmit={handleSubmit}
            className="w-full border p-4 rounded-lg"
          >
            <div className="flex flex-col lg:flex-row gap-4 items-start">
              <div className="w-full lg:w-2/3 space-y-2">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.companyNameLabel")}
                      value={form.name}
                      placeholder={t("becomeProvider.companyNamePlaceholder")}
                      onChange={(name, value) => handleChange("name", value)}
                      required
                      icon={
                        <BuildingOfficeIcon className="w-5 h-5 text-gray-500" />
                      }
                    />
                  </div>
                  <div className="w-full sm:w-1/2">
                    <Autocomplete
                      onSelect={(e) => {
                        handleChange("location", e);
                        handleChange("address", e.formattedAddress);
                      }}
                      initialAddress={form.address || ""}
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.contactEmailLabel")}
                      value={form.contactEmail || ""}
                      placeholder={t("becomeProvider.contactEmailPlaceholder")}
                      onChange={(name, value) =>
                        handleChange("contactEmail", value)
                      }
                      required
                    />
                  </div>
                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.phoneNumberLabel")}
                      value={form.phoneNumber}
                      placeholder={t("becomeProvider.phoneNumberPlaceholder")}
                      onChange={(name, value) =>
                        handleChange("phoneNumber", value)
                      }
                      icon={<PhoneIcon className="w-5 h-5 text-gray-500" />}
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.contactPersonLabel")}
                      value={form.contactPerson}
                      placeholder={t("becomeProvider.contactPersonPlaceholder")}
                      onChange={(name, value) =>
                        handleChange("contactPerson", value)
                      }
                      icon={
                        <UserCircleIcon className="w-5 h-5 text-gray-500" />
                      }
                      required
                    />
                  </div>
                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.neqNumberLabel")}
                      value={form.neqNumber}
                      placeholder={t("becomeProvider.neqNumberPlaceholder")}
                      onChange={(name, value) =>
                        handleChange("neqNumber", value)
                      }
                      icon={
                        <IdentificationIcon className="w-5 h-5 text-gray-500" />
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.websiteLabel")}
                      value={form.website}
                      placeholder={t("becomeProvider.websitePlaceholder")}
                      onChange={(name, value) => handleChange("website", value)}
                      icon={<GlobeAltIcon className="w-5 h-5 text-gray-500" />}
                    />
                  </div>
                  <div className="w-full sm:w-1/2" />
                </div>

                <div className="form-control">
                  <Toggle
                    isEnabled={form.isActive}
                    onToggle={(newValue) => {
                      if (!newValue) {
                        const confirmed = window.confirm(
                          t("adminUserTable.confirmDeactivate")
                        );
                        if (!confirmed) return;
                      }
                      setForm((prev) => ({ ...prev, isActive: newValue }));
                    }}
                    labelOn={t("adminUserTable.companyActive")}
                    labelOff={t("adminUserTable.companyInactive")}
                    colorActive="bg-green-500"
                    colorInactive="bg-red-500"
                    className="toggle-lg"
                    title={t("adminUserTable.companyStatus")}
                    name="isActive"
                  />
                </div>
              </div>

              <div className="w-full lg:w-1/3 lg:sticky lg:top-4 self-start">
                <div className="max-h-[520px] overflow-auto">
                  <ImageUploader
                    ratio="square"
                    initialImageUrl={form.tempImageUrl || ""}
                    onTempImageChange={(url) => {
                      setForm((prev) => ({
                        ...prev,
                        tempImageUrl: url?.trim() || "",
                        hasChangedImageUrl: true,
                      }));
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-4 border-t pt-3">
              <button
                type="submit"
                className="px-6 py-3 rounded-full bg-primary text-white hover:bg-accent"
              >
                {t("modal.save")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
